import { cn } from '@ui/lib/utils';
import React from 'react';

export const BadgeWithBorder = ({
  color,
  children,
  className,
  iconLeft,
  iconRight,
  iconFill,
  iconStroke,
  animateIcon = false,
  showIcon = true,
  size = 'default',
  variant = 'default',
  blipActive = false,
  blipColor,
}: {
  color: string;
  children: React.ReactNode;
  className?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  iconFill?: string;
  iconStroke?: string;
  animateIcon?: boolean;
  showIcon?: boolean;
  size?: 'default' | 'xs' | 'sm';
  variant?: 'default' | 'filled' | 'muted-filled';
  blipActive?: boolean;
  blipColor?: string;
}) => {
  // Standard color variants (outlined with colored text)
  const colorVariants: { [key: string]: string } = {
    pink: 'ring-pink-700 ring-opacity-5 bg-[#fff1fc] text-[#a91e6b]',
    blue: 'ring-blue-700 ring-opacity-5 bg-blue-50    text-blue-800',
    green: 'ring-green-700 ring-opacity-5 bg-green-50   text-green-800',
    red: 'ring-red-700 ring-opacity-5 bg-red-50     text-red-800',
    indigo: 'ring-indigo-700 ring-opacity-5 bg-indigo-50 text-indigo-800',
    amber: 'ring-amber-800 ring-opacity-5 bg-amber-50   text-amber-800',
    fuchsia: 'ring-fuchsia-700 ring-opacity-5 bg-fuchsia-50 text-fuchsia-800',
    slate: 'ring-slate-800 ring-opacity-5 bg-slate-50   text-slate-900',
    gray: 'ring-slate-800 ring-opacity-5 bg-slate-50   text-slate-900',
    zinc: 'ring-zinc-800 ring-opacity-5 bg-zinc-50    text-zinc-800',
    neutral: 'ring-slate-800 ring-opacity-5 bg-slate-50   text-slate-800',
    white: 'ring-slate-800 ring-opacity-5 bg-white      text-slate-900',
    stone: 'ring-stone-800 ring-opacity-5 bg-stone-50   text-stone-800',
    lime: 'ring-lime-800 ring-opacity-5 bg-lime-50    text-lime-800',
    yellow: 'ring-yellow-800 ring-opacity-5 bg-yellow-50  text-yellow-800',
    orange: 'ring-orange-800 ring-opacity-5 bg-orange-50  text-orange-800',
    emerald: 'ring-emerald-700 ring-opacity-5 bg-green-50   text-green-800',
    teal: 'ring-teal-700 ring-opacity-5 bg-teal-50    text-teal-800',
    cyan: 'ring-cyan-700 ring-opacity-5 bg-cyan-50    text-cyan-800',
    sky: 'ring-sky-700 ring-opacity-5 bg-sky-50     text-sky-800',
    purple: 'ring-purple-700 ring-opacity-5 bg-purple-50  text-purple-800',
    violet: 'ring-violet-700 ring-opacity-5 bg-violet-50  text-violet-800',
    rose: 'ring-rose-700 ring-opacity-5 bg-rose-50    text-rose-800',

    // Petboost brand overrides
    '#325ee2': 'ring-blue-700 ring-opacity-5 bg-blue-50    text-blue-800',
    '#b15500': 'ring-orange-800 ring-opacity-5 bg-orange-50  text-orange-800',
    '#cc0000': 'ring-red-700 ring-opacity-5 bg-red-50     text-red-800',
    '#c7328b': 'ring-pink-700 ring-opacity-5 bg-pink-50    text-pink-800',
    '#0b7e64': 'ring-green-700 ring-opacity-5 bg-green-50   text-green-800',
    pbred: 'ring-red-700 ring-opacity-5 bg-red-50 text-[#ff5349]', // Petboost Brand Red (accessible, darker)
  };

  // Filled color variants (colored background with white text)
  const filledColorVariants: { [key: string]: string } = {
    pink: 'bg-[#c7328b] text-white ring-0',
    blue: 'bg-blue-700 text-white ring-0',
    green: 'bg-green-700 text-white ring-0',
    red: 'bg-red-700 text-white ring-0',
    indigo: 'bg-indigo-700 text-white ring-0',
    amber: 'bg-amber-800 text-white ring-0',
    fuchsia: 'bg-fuchsia-700 text-white ring-0',
    slate: 'bg-slate-700 text-white ring-0',
    gray: 'bg-slate-700 text-white ring-0',
    zinc: 'bg-zinc-700 text-white ring-0',
    neutral: 'bg-slate-700 text-white ring-0',
    stone: 'bg-stone-700 text-white ring-0',
    lime: 'bg-lime-800 text-white ring-0',
    yellow: 'bg-yellow-800 text-white ring-0',
    orange: 'bg-orange-700 text-white ring-0',
    emerald: 'bg-emerald-700 text-white ring-0',
    teal: 'bg-teal-700 text-white ring-0',
    cyan: 'bg-cyan-700 text-white ring-0',
    sky: 'bg-sky-700 text-white ring-0',
    purple: 'bg-purple-700 text-white ring-0',
    violet: 'bg-violet-700 text-white ring-0',
    rose: 'bg-rose-700 text-white ring-0',
    '#325ee2': 'bg-blue-700 text-white ring-0', // Petboost Royal Blue
    '#b15500': 'bg-orange-700 text-white ring-0', // Petboost Orange
    '#cc0000': 'bg-red-700 text-white ring-0', // Petboost Critical Red
    '#c7328b': 'bg-pink-700 text-white ring-0', // Petboost Warning Amber
    '#0b7e64': 'bg-green-700 text-white ring-0', // Petboost Mint Green
    pbred: 'bg-[#F84435] text-white ring-0', // Petboost Brand Red (accessible)
  };

  // Muted-filled color variants (70% saturation, 120% contrast, darker text)
  const mutedFilledColorVariants: { [key: string]: string } = {
    pink: 'bg-[#d4a1c0] text-[#430f2d] ring-0',
    blue: 'bg-[#8ca4d9] text-[#11204d] ring-0',
    green: 'bg-[#8abe9b] text-[#133d1f] ring-0',
    red: 'bg-[#d8a0a0] text-[#521313] ring-0',
    indigo: 'bg-[#b6a2d3] text-[#2a1947] ring-0',
    amber: 'bg-[#d9ba8c] text-[#4a330f] ring-0',
    fuchsia: 'bg-[#d0a2cb] text-[#471343] ring-0',
    slate: 'bg-[#a4acb9] text-[#1a1f2a] ring-0',
    gray: 'bg-[#a4acb9] text-[#1a1f2a] ring-0',
    zinc: 'bg-[#a4acb9] text-[#1e1e21] ring-0',
    neutral: 'bg-[#a4acb9] text-[#1a1f2a] ring-0',
    stone: 'bg-[#b1aea8] text-[#2e2922] ring-0',
    lime: 'bg-[#b9bf95] text-[#2f3714] ring-0',
    yellow: 'bg-[#e2d192] text-[#4d420a] ring-0',
    orange: 'bg-[#dda980] text-[#5a1f00] ring-0',
    emerald: 'bg-[#8abe9b] text-[#133d1f] ring-0',
    teal: 'bg-[#85c2bf] text-[#0d3b39] ring-0',
    cyan: 'bg-[#8ac7d3] text-[#093843] ring-0',
    sky: 'bg-[#8cbfde] text-[#073354] ring-0',
    purple: 'bg-[#a2a4d8] text-[#1c2051] ring-0',
    violet: 'bg-[#b8a2d3] text-[#311847] ring-0',
    rose: 'bg-[#dba4b1] text-[#511421] ring-0',
    '#325ee2': 'bg-[#8ca4d9] text-[#11204d] ring-0', // Petboost Royal Blue (muted)
    '#b15500': 'bg-[#dda980] text-[#5a1f00] ring-0', // Petboost Orange (muted)
    '#cc0000': 'bg-[#d8a0a0] text-[#521313] ring-0', // Petboost Critical Red (muted)
    '#c7328b': 'bg-[#d4a1c0] text-[#430f2d] ring-0', // Petboost Warning Amber (muted)
    '#0b7e64': 'bg-[#8abe9b] text-[#133d1f] ring-0', // Petboost Mint Green (muted)
    pbred: 'bg-[#f4a497] text-[#62130a] ring-0', // Petboost Brand Red (muted, accessible)
  };

  // Proportional icon size variants
  const iconSizeVariants = {
    default: 'h-2.5 w-2.5',
    sm: 'h-2.5 w-2.5',
    xs: 'h-2 w-2',
  };

  // Determine if we should apply ring styling (not for xs size)
  const shouldHaveRing = size !== 'xs';

  // Prepare icon styling - but preserve any existing className on the icon
  const iconStyling = cn(animateIcon && 'animate-pulse', [
    iconFill ? `fill-${iconFill}` : '',
    iconStroke ? `stroke-${iconStroke}` : '',
  ]);

  // Apply styling to the icon if it's a React element, but preserve original className
  const renderIcon = (icon: React.ReactNode) => {
    if (!showIcon) return null;

    if (React.isValidElement(icon)) {
      const iconElement = icon as React.ReactElement;
      // Get the original className from the icon element if it exists
      const originalClassName =
        typeof iconElement.props === 'object' && iconElement.props !== null
          ? ((iconElement.props as { className?: string }).className ?? '')
          : '';

      // If the original className already has size classes, use those
      // Otherwise use size-specific icon dimensions
      const hasCustomSize =
        originalClassName.includes('h-') || originalClassName.includes('w-');
      const sizeClasses = hasCustomSize ? '' : iconSizeVariants[size];

      return React.cloneElement(iconElement, {
        className: cn(originalClassName, sizeClasses, iconStyling),
      } as React.HTMLAttributes<HTMLElement>);
    }
    return icon;
  };

  // Select appropriate color variant based on variant type
  let selectedColorVariant;
  switch (variant) {
    case 'filled':
      selectedColorVariant = filledColorVariants[color];
      break;
    case 'muted-filled':
      selectedColorVariant = mutedFilledColorVariants[color];
      break;
    default:
      selectedColorVariant = colorVariants[color];
  }

  // Adjust padding based on icon presence
  const hasLeftIcon = iconLeft && showIcon;
  const hasRightIcon = iconRight && showIcon;

  // Determine padding classes based on icon presence
  let paddingClasses = '';
  if (size === 'default' || size === 'sm') {
    if (hasLeftIcon && hasRightIcon)
      paddingClasses = '-ml-[1px] pl-1 pr-1 py-[4px]';
    else if (hasLeftIcon) paddingClasses = '-ml-[1px] pl-1 pr-1.5 py-[4px]';
    else if (hasRightIcon) paddingClasses = '-ml-[1px] pl-1.5 pr-1 py-[4px]';
    else paddingClasses = '-ml-[1px] px-1.5 py-[4px]';
  } else if (size === 'xs') {
    if (hasLeftIcon && hasRightIcon) paddingClasses = 'pl-0.5 pr-0.5 py-0.5';
    else if (hasLeftIcon) paddingClasses = 'pl-0.5 pr-1 py-0.5';
    else if (hasRightIcon) paddingClasses = 'pl-1 pr-0.5 py-0.5';
    else paddingClasses = 'px-1 py-0.5';
  }

  return (
    <span
      className={cn(
        'inline-flex items-center text-center rounded-md whitespace-nowrap --tracking-[0.1px] relative text-[11px] leading-none font-bold ',
        // Only apply ring-1 and ring-inset if not xs size
        shouldHaveRing && 'ring-1 ring-inset',
        selectedColorVariant,
        // Force XS specific styles
        size === 'xs' && 'text-xs !leading-[1.1] font-bold',
        // Use dynamic padding instead of sizeVariants
        paddingClasses,
        // Apply styles for default size
        size === 'default' &&
          'text-xs leading-none -tracking-[0.1px] font-bold',
        // Apply styles for sm size
        size === 'sm' && 'text-xs leading-none -tracking-[0.1px] font-bold',
        // Only apply font-bold to filled variants
        (variant === 'filled' || variant === 'muted-filled') && 'font-bold',
        className
      )}
    >
      {iconLeft && showIcon && (
        <span className="inline-flex mr-[2px]">{renderIcon(iconLeft)}</span>
      )}
      {children}
      {iconRight && showIcon && (
        <span className="inline-flex ml-0.5">{renderIcon(iconRight)}</span>
      )}

      {/* Animated SVG Blip Indicator */}
      {blipActive && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="absolute top-[-3.5px] right-[-4px]"
          width="10px"
          height="10px"
          viewBox="0 0 32 32"
        >
          <circle
            fill={blipColor || '#c7328b'}
            stroke="none"
            cx="14"
            cy="14"
            r="14"
          >
            <animate
              attributeName="opacity"
              dur="1.2s"
              values="0;1;0"
              repeatCount="indefinite"
              begin="0.1"
            />
          </circle>
        </svg>
      )}
    </span>
  );
};
